import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PickLine, PickOrder, PickResult } from '@popcart/core/v1';
import { api, errorMessage, errorStatus } from '../../api';
import { ExpressChip, Loading, StatusPill, useTitle } from '../../ui/bits';
import { Fig, Stat } from '../../ui/viz';
import { ProductArt, Scene, SceneError } from '../../catalog/art';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/toast';
import { clock, count, peso } from '../../lib/format.js';
import { QUEUE_MS, shopKeys, useShop } from '../ShopLayout';
import {
  applyResults,
  applyScan,
  foundCount,
  fullName,
  isLocked,
  isUnpickedConflict,
  isUnweighedConflict,
  leftToPick,
  lineForProduct,
  normalizeCode,
  orderAction,
  otherLinesText,
  resultLabel,
  savedMessage,
  selectOrder,
  settlementText,
  sortOrders,
  subLabel,
  subMode,
  toPickCount,
} from './lib/pick.js';
import { martKeys } from './keys';
import { SubstituteSheet } from './SubstituteSheet';
import './mart.css';

/** /shop/:shopId/mart — the Grocery pick list (data key `mart`): orders Express first, lines in shelf order, scan to tick. */
export function MartPage() {
  const { shopId } = useShop();
  useTitle('Grocery · pick list');
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: martKeys.pickList(shopId),
    queryFn: () => api.pickList(shopId),
    refetchInterval: QUEUE_MS,
    refetchIntervalInBackground: true,
    enabled: !!shopId,
  });
  // Results saved on this screen, shown before the next poll lands (server stays authoritative).
  const [results, setResults] = useState<Record<string, PickResult>>({});
  // The order's money once its last Mart line is picked, by shopOrderId.
  const [settled, setSettled] = useState<Record<string, PickResult>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [announce, setAnnounce] = useState('');
  const orders = useMemo(() => applyResults(sortOrders(query.data ?? []), results), [query.data, results]);
  const order = selectOrder(orders, selectedId);

  const onSaved = (o: PickOrder, l: PickLine, r: PickResult) => {
    setResults((m) => ({ ...m, [l.orderItemId]: r }));
    if (r.allPicked) setSettled((m) => ({ ...m, [o.shopOrderId]: r }));
    else
      setSettled((m) => {
        if (!m[o.shopOrderId]) return m;
        const { [o.shopOrderId]: _drop, ...rest } = m;
        return rest;
      });
    setAnnounce(savedMessage(l, r));
    void qc.invalidateQueries({ queryKey: martKeys.pickList(shopId) });
    void qc.invalidateQueries({ queryKey: shopKeys.queue(shopId) });
  };
  const onConflict = () => void qc.invalidateQueries({ queryKey: ['shop', shopId] });

  return (
    <>
      <header className="dv-head">
        <div className="stack s4">
          <h1>Grocery · pick list</h1>
          <span className="dv-live" title="Express first, then oldest. Lines in shelf order.">
            Lines in shelf order
          </span>
        </div>
      </header>
      {query.data && orders.length ? (
        <div className="dv-stats mp-stats dv-rise">
          <Stat tone="ink" label="Lines to pick" value={count(toPickCount(orders))} visual={<span className="dv-disc mp-disc" aria-hidden="true"><Icon name="list" /></span>} />
          <Stat label="Orders" value={count(orders.length)} />
          <Stat label="All picked" value={count(orders.filter((o) => !leftToPick(o)).length)} />
        </div>
      ) : null}
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announce}
      </p>
      {query.isError ? (
        <section className="card">
          <SceneError error={query.error} onRetry={() => void query.refetch()} />
        </section>
      ) : query.isLoading ? (
        <Loading label="Loading the pick list" rows={3} />
      ) : !orders.length || !order ? (
        <section className="card">
          <Scene scene="emptyCart" title="All picked. Nice and quiet.">
            Grocery orders show up here once they’re accepted, Express first.
          </Scene>
        </section>
      ) : (
        <div className="mp">
          <nav className="card dv-card mp-queue dv-rise" style={{ ['--i' as string]: 1 }} aria-label="Orders to pick">
            {orders.map((o) => (
              <OrderButton key={o.shopOrderId} order={o} current={o.shopOrderId === order.shopOrderId} onSelect={() => setSelectedId(o.shopOrderId)} />
            ))}
          </nav>
          <OrderPanel
            key={order.shopOrderId}
            shopId={shopId}
            order={order}
            settled={settled[order.shopOrderId] ?? null}
            onSaved={onSaved}
            onConflict={onConflict}
            onAnnounce={setAnnounce}
          />
        </div>
      )}
    </>
  );
}

function OrderButton({ order, current, onSelect }: { order: PickOrder; current: boolean; onSelect(): void }) {
  const left = leftToPick(order);
  return (
    <button type="button" className={`mp-order${left ? '' : ' done'}`} aria-current={current ? 'true' : undefined} onClick={onSelect}>
      <span className="mp-order-main">
        <span className="row">
          <b className="ph-code">{order.code}</b>
          {order.speed === 'express' ? <ExpressChip /> : null}
        </span>
        <span className="dv-sub">
          {order.customerFirstName} · {clock(order.placedAt)}
        </span>
        <span className="dv-share" aria-hidden="true">
          <i style={{ width: `${order.lines.length ? ((order.lines.length - left) / order.lines.length) * 100 : 0}%` }} />
        </span>
      </span>
      {left ? (
        <span className="mp-order-n">
          <Fig text={String(left)} size="md" animate={false} />
          <small>to pick</small>
        </span>
      ) : (
        <StatusPill tone="success">Picked</StatusPill>
      )}
    </button>
  );
}

function OrderPanel({
  shopId,
  order,
  settled,
  onSaved,
  onConflict,
  onAnnounce,
}: {
  shopId: string;
  order: PickOrder;
  settled: PickResult | null;
  onSaved(o: PickOrder, l: PickLine, r: PickResult): void;
  onConflict(): void;
  onAnnounce(s: string): void;
}) {
  const locked = isLocked(order);
  // Found counts on screen (scans and the stepper), not saved yet.
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [sheetFor, setSheetFor] = useState<string | null>(null);
  const [scanMsg, setScanMsg] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const scanRef = useRef<HTMLInputElement>(null);
  const others = otherLinesText(order.otherLines);
  const sheetLine = order.lines.find((l) => l.orderItemId === sheetFor) ?? null;

  const pick = useMutation({
    mutationFn: ({ line, pickedQty, substitute }: { line: PickLine; pickedQty: number; substitute: { productId: string; qty: number } | null }) =>
      api.pickItem(shopId, line.orderItemId, { pickedQty, substitute }),
    onSuccess: (r, v) => {
      setCounts(({ [v.line.orderItemId]: _drop, ...rest }) => rest);
      setSheetFor(null);
      setScanMsg((m) => (m?.tone === 'error' ? null : m));
      onSaved(order, v.line, r);
    },
  });

  const savePick = (line: PickLine, pickedQty: number, substitute: { productId: string; qty: number } | null = null) =>
    pick.mutateAsync({ line, pickedQty, substitute });

  const pickError = pick.error ? (errorStatus(pick.error) === 409 ? errorMessage(pick.error, 'This order has moved on and can’t be picked again.') : errorMessage(pick.error, 'Couldn’t save the pick. Try again.')) : null;
  useEffect(() => {
    if (pick.error && errorStatus(pick.error) === 409) onConflict();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pick.error]);

  // Keyboard-wedge scanners type wherever focus is: send stray keystrokes to the scan field.
  useEffect(() => {
    if (locked) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.closest('input,textarea,select,[contenteditable="true"]') || t.closest('dialog[open]'))) return;
      if (document.querySelector('dialog[open]')) return;
      scanRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [locked]);

  // Fine pointers (a tablet with a scanner, a desk) start in the scan field; phones don't pop the keyboard.
  useEffect(() => {
    if (!locked && window.matchMedia?.('(pointer: fine)').matches) scanRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tickLine = (line: PickLine, count: number, complete: boolean) => {
    setFlash(line.orderItemId);
    window.setTimeout(() => setFlash((f) => (f === line.orderItemId ? null : f)), 900);
    document.getElementById(`mp-line-${line.orderItemId}`)?.scrollIntoView?.({ block: 'nearest' });
    if (complete) {
      setScanMsg({ tone: 'info', text: `${fullName(line)}: ${count} of ${line.qty}. Saving as picked.` });
      setCounts((c) => ({ ...c, [line.orderItemId]: count }));
      void savePick(line, line.qty).catch(() => undefined);
    } else {
      setCounts((c) => ({ ...c, [line.orderItemId]: count }));
      setScanMsg({ tone: 'info', text: `${fullName(line)}: ${count} of ${line.qty}.` });
    }
    onAnnounce(`${fullName(line)}, ${count} of ${line.qty}.`);
  };

  const lookup = useMutation({
    mutationFn: (code: string) => api.barcodeLookup(shopId, code),
  });

  const onScan = async (e: FormEvent) => {
    e.preventDefault();
    const input = scanRef.current;
    if (!input) return;
    const raw = input.value;
    input.value = '';
    const r = applyScan(order.lines, counts, raw);
    if (r.kind === 'empty') return;
    if (r.kind === 'tick') {
      const line = order.lines.find((l) => l.orderItemId === r.orderItemId);
      if (line) tickLine(line, r.count, r.complete);
      return;
    }
    if (r.kind === 'full') {
      const line = order.lines.find((l) => l.orderItemId === r.orderItemId);
      setScanMsg({ tone: 'error', text: `${line ? fullName(line) : 'That item'}: all ${line?.qty ?? ''} already counted. Put the extra back.` });
      return;
    }
    // Not on any line's barcode: ask the server what it is.
    try {
      const p = await lookup.mutateAsync(r.code);
      const line = lineForProduct(order.lines, p.productId);
      if (line) {
        const now = foundCount(line, counts);
        if (now >= line.qty) setScanMsg({ tone: 'error', text: `${fullName(line)}: all ${line.qty} already counted.` });
        else tickLine(line, now + 1, now + 1 === line.qty);
      } else setScanMsg({ tone: 'error', text: `${fullName(p)} isn’t on ${order.code}. Check the item.` });
    } catch (err) {
      setScanMsg({ tone: 'error', text: errorStatus(err) === 404 ? `Unknown code ${normalizeCode(raw)}. Check it, or tick the line by hand.` : errorMessage(err, 'Couldn’t look up that code. Try again.') });
    }
  };

  const picked = order.lines.length - leftToPick(order);

  return (
    <section className="card dv-card mp-panel dv-rise" style={{ ['--i' as string]: 2 }} aria-labelledby="mp-order-h">
      <div className="mp-panel-h">
        <div className="stack s4" style={{ minWidth: 0 }}>
          <h2 id="mp-order-h">
            <span className="ph-code">{order.code}</span>
            {order.customerFirstName ? ` · ${order.customerFirstName}` : ''}
          </h2>
          <span className="dv-sub">Placed {clock(order.placedAt)}</span>
        </div>
        <div className="row wrap">
          {order.speed === 'express' ? <ExpressChip /> : null}
          <span className="mp-progress" aria-label={`${picked} of ${order.lines.length} picked`}>
            <Fig text={`${picked} / ${order.lines.length}`} size="md" />
            <span className="dv-label">picked</span>
          </span>
        </div>
      </div>
      {order.note ? (
        <div className="ph-note">
          <span className="dv-label">Customer note</span>
          <p>{order.note}</p>
        </div>
      ) : null}

      {locked ? null : (
        <form className="mp-scan" onSubmit={(e) => void onScan(e)} role="search" aria-label="Scan a barcode">
          <label className="search">
            <Icon name="search" />
            <span className="sr-only">Scan or type a barcode, then Enter</span>
            <input ref={scanRef} inputMode="numeric" autoComplete="off" enterKeyHint="go" placeholder="Scan or type a barcode" aria-describedby="mp-scan-msg" />
          </label>
          <button type="submit" className="btn btn-quiet" disabled={lookup.isPending}>
            Add
          </button>
          <span id="mp-scan-msg" className={scanMsg?.tone === 'error' ? 'pill st-error mp-wrap' : 'dv-sub'} role={scanMsg?.tone === 'error' ? 'alert' : undefined}>
            {scanMsg ? scanMsg.text : 'Each scan counts one · a full line saves'}
          </span>
        </form>
      )}

      {pickError ? (
        <div className="status-msg st-error" role="alert">
          {pickError}
        </div>
      ) : null}

      <ol className="mp-lines" aria-label={`${order.code} items in shelf order`}>
        {order.lines.map((l) => (
          <li key={l.orderItemId}>
            <LineCard
              line={l}
              count={foundCount(l, counts)}
              counting={typeof counts[l.orderItemId] === 'number'}
              flash={flash === l.orderItemId}
              locked={locked}
              busy={pick.isPending && pick.variables?.line.orderItemId === l.orderItemId}
              onCount={(n) => setCounts((c) => ({ ...c, [l.orderItemId]: n }))}
              onPicked={() => void savePick(l, l.qty).catch(() => undefined)}
              onShort={() => {
                pick.reset();
                setSheetFor(l.orderItemId);
              }}
            />
          </li>
        ))}
      </ol>

      {sheetLine ? (
        <SubstituteSheet
          shopId={shopId}
          line={sheetLine}
          initialFound={Math.min(foundCount(sheetLine, counts), sheetLine.qty - 1)}
          busy={pick.isPending}
          error={pickError}
          onSave={(pickedQty, sub) => void savePick(sheetLine, pickedQty, sub).catch(() => undefined)}
          onClose={() => setSheetFor(null)}
        />
      ) : null}

      <OrderFooter shopId={shopId} order={order} others={others} settled={settled} onAnnounce={onAnnounce} />
    </section>
  );
}

function LineCard({
  line,
  count,
  counting,
  flash,
  locked,
  busy,
  onCount,
  onPicked,
  onShort,
}: {
  line: PickLine;
  count: number;
  counting: boolean;
  flash: boolean;
  locked: boolean;
  busy: boolean;
  onCount(n: number): void;
  onPicked(): void;
  onShort(): void;
}) {
  const [editing, setEditing] = useState(false);
  const done = !!line.picked;
  const open = !locked && (!done || editing || counting);
  const result = resultLabel(line);
  const mode = subMode(line);
  const name = fullName(line);
  return (
    <article id={`mp-line-${line.orderItemId}`} className={`mp-line${done ? ' done' : ''}${flash ? ' flash' : ''}`} aria-label={name}>
      <ProductArt className="mp-thumb" src={line.imageUrl} name={line.name} line="mart" />
      <div className="stack s4 mp-body">
        <div className="mp-line-h">
          <h3>
            {line.name}
            {line.variantLabel ? <span className="mp-variant"> · {line.variantLabel}</span> : null}
          </h3>
          <span className="mp-qty" role="img" aria-label={`Quantity ${line.qty}`}>
            <Fig text={`×${line.qty}`} size="lg" animate={false} />
          </span>
        </div>
        <span className="mp-meta">
          <span className="mp-shelf" title="Shelf">
            {line.shelf ?? '—'}
          </span>
          <span className="dv-sub">{[line.barcode ?? 'No barcode', `${peso(line.unitCentavos)} each`].join(' · ')}</span>
        </span>
        <span className="row wrap">
          <span className="dv-chip">
            <Icon name={mode === 'none' ? 'close' : 'swap'} size="sm" />
            {subLabel(line)}
          </span>
          {result ? (
            <StatusPill tone={line.picked?.status === 'picked' ? 'success' : line.picked?.status === 'substituted' ? 'info' : 'warning'}>{result}</StatusPill>
          ) : null}
          {line.picked && line.picked.deltaCentavos < 0 ? <span className="dv-sub">{peso(-line.picked.deltaCentavos)} back</span> : null}
        </span>
        {open ? (
          <div className="row wrap mp-actions">
            <div className="stepper" role="group" aria-label={`Found of ${name}`}>
              <button type="button" className="btn icon-btn" aria-label={`One fewer ${name}`} disabled={count <= 0 || busy} onClick={() => onCount(count - 1)}>
                <Icon name="minus" />
              </button>
              <output aria-live="polite" aria-label={`${count} of ${line.qty} found`}>
                {count}/{line.qty}
              </output>
              <button type="button" className="btn icon-btn" aria-label={`One more ${name}`} disabled={count >= line.qty || busy} onClick={() => onCount(count + 1)}>
                <Icon name="plus" />
              </button>
            </div>
            <span className="grow" />
            <button type="button" className="btn btn-quiet" disabled={busy} aria-busy={busy || undefined} onClick={onShort}>
              <Icon name="alert" size="sm" />
              Short
            </button>
            <button
              type="button"
              className="btn"
              disabled={busy}
              aria-busy={busy || undefined}
              onClick={() => {
                setEditing(false);
                onPicked();
              }}
            >
              <Icon name="check" size="sm" />
              {busy ? 'Saving…' : `Picked ${line.qty}`}
            </button>
          </div>
        ) : done && !locked ? (
          <div className="row">
            <button type="button" className="btn btn-quiet" onClick={() => setEditing(true)}>
              Re-pick
            </button>
          </div>
        ) : null}
      </div>
    </article>
  );
}

function OrderFooter({ shopId, order, others, settled, onAnnounce }: { shopId: string; order: PickOrder; others: string; settled: PickResult | null; onAnnounce(s: string): void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [problem, setProblem] = useState<string | null>(null);
  const action = orderAction(order);
  const btn = useRef<HTMLButtonElement>(null);
  const wasReady = useRef(action.kind === 'ready');
  // When the last line is picked the Volt appears; move focus to it so Enter keeps the flow going.
  useEffect(() => {
    if (action.kind === 'ready' && !wasReady.current) btn.current?.focus();
    wasReady.current = action.kind === 'ready';
  }, [action.kind]);

  const ready = useMutation({
    mutationFn: async (steps: ('accepted' | 'preparing' | 'ready')[]) => {
      for (const to of steps) await api.advanceShopOrder(shopId, order.shopOrderId, to);
    },
    onSuccess: () => {
      setProblem(null);
      const msg = `${order.code} is ready for the rider.`;
      toast(msg);
      onAnnounce(msg);
      void qc.invalidateQueries({ queryKey: ['shop', shopId] });
    },
    onError: (err) => {
      setProblem(
        isUnpickedConflict(err)
          ? 'Pick every Grocery item first. Someone may have changed this order; the list has been refreshed.'
          : isUnweighedConflict(err)
            ? 'Weigh every Fresh item first, at the Fresh station.'
            : errorMessage(err, "Couldn't update the order. Try again."),
      );
      void qc.invalidateQueries({ queryKey: ['shop', shopId] });
    },
  });

  const money = settled ? settlementText(settled) : null;

  return (
    <div className="stack mp-foot">
      {money ? (
        <div className="ph-note mp-money" role="status">
          <span className="row">
            <Icon name="cash" size="sm" />
            <b>{money}</b>
          </span>
        </div>
      ) : null}
      {problem ? (
        <div className="status-msg st-error" role="alert">
          {problem}
        </div>
      ) : null}
      <div className="row between wrap">
        <span className="dv-sub" style={{ maxWidth: '44ch' }}>
          {action.kind === 'pick'
            ? `${action.left} Grocery ${action.left === 1 ? 'item' : 'items'} left to pick`
            : action.kind === 'done'
              ? 'Packed · waiting for the rider'
              : action.kind === 'unknown'
                ? 'All picked · finish the rest on Today'
                : `All picked.${others ? ` Check the ${others} items too.` : ''}`}
        </span>
        {action.kind === 'done' ? null : (
          <button
            ref={btn}
            type="button"
            className="btn btn-primary btn-lg"
            disabled={action.kind !== 'ready' || ready.isPending}
            aria-busy={ready.isPending || undefined}
            onClick={() => action.kind === 'ready' && ready.mutate(action.steps)}
          >
            {ready.isPending ? 'Saving…' : 'Ready for rider'}
          </button>
        )}
      </div>
    </div>
  );
}
